import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EMAIL_TEMPLATES from "../../data/emailTemplates";
import { getPosStoreSettings } from "../../services/posSettingsService";

const SAMPLE_VALUES = {
  "{{name}}": "Alex Chen",
  "{{email}}": "alex@store.com",
  "{{password}}": "TempPass123",
  "{{storeName}}": "Harbour Cafe",
  "{{ticketTitle}}": "POS till offline at register 2",
  "{{raisedBy}}": "Sam Patel",
};

function fillSample(text, storeName) {
  const values = {
    ...SAMPLE_VALUES,
    "{{storeName}}": storeName || SAMPLE_VALUES["{{storeName}}"],
  };
  return Object.entries(values).reduce(
    (value, [token, sample]) => value.split(token).join(sample),
    text
  );
}

export default function EmailTemplates() {
  const theme = useTheme();
  const [selectedId, setSelectedId] = useState(EMAIL_TEMPLATES[0].id);
  const [storeName, setStoreName] = useState(SAMPLE_VALUES["{{storeName}}"]);
  const [logoUrl, setLogoUrl] = useState("");
  const selected = EMAIL_TEMPLATES.find((item) => item.id === selectedId);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await getPosStoreSettings();
        if (cancelled) return;
        if (settings?.store_name) setStoreName(settings.store_name);
        setLogoUrl(settings?.logo_url || "");
      } catch {
        if (!cancelled) setLogoUrl("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fillPreview = (text) => fillSample(text, storeName);

  return (
    <Box sx={{ p: 4, maxWidth: 1100, mx: "auto" }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, letterSpacing: "-0.4px" }}
        >
          Email Templates
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 0.5, color: "text.secondary" }}
        >
          Preview the emails Envoy sends and the action that triggers each one.
          This page is view-only.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <Card
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 2, py: 1.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Templates
            </Typography>
          </Box>
          <Divider />
          <List disablePadding>
            {EMAIL_TEMPLATES.map((item, index) => (
              <React.Fragment key={item.id}>
                {index > 0 && <Divider />}
                <ListItemButton
                  selected={item.id === selectedId}
                  onClick={() => setSelectedId(item.id)}
                  sx={{
                    alignItems: "flex-start",
                    py: 1.5,
                    "&.Mui-selected": {
                      backgroundColor: "rgba(37, 99, 235, 0.08)",
                    },
                  }}
                >
                  <ListItemText
                    primary={item.name}
                    secondary={item.action}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                    secondaryTypographyProps={{
                      sx: { mt: 0.35, fontSize: 12.5 },
                    }}
                  />
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        </Card>

        {selected && (
          <Stack spacing={2}>
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  {selected.name}
                </Typography>
                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Sent when
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selected.action}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Sent to
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selected.recipient}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Card>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                overflow: "hidden",
                backgroundColor: "#f3f4f6",
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 1.25,
                  backgroundColor: "#e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Email preview
                </Typography>
                <Chip label="Sample data" size="small" />
              </Box>

              <Box
                sx={{
                  m: 2,
                  mb: 2.5,
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.08)",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                  <Typography variant="caption" color="text.secondary">
                    From
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {selected.from}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Subject
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {fillPreview(selected.subject)}
                  </Typography>
                </Box>

                <Box sx={{ px: 2.5, py: 2.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      mb: 2,
                      minHeight: 48,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        fontSize: 12,
                      }}
                    >
                      {storeName}
                    </Typography>
                    {logoUrl ? (
                      <Box
                        component="img"
                        src={logoUrl}
                        alt={storeName}
                        sx={{
                          maxHeight: 48,
                          maxWidth: 96,
                          width: "auto",
                          height: "auto",
                          objectFit: "contain",
                          display: "block",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box sx={{ width: 96, height: 48, flexShrink: 0 }} aria-hidden />
                    )}
                  </Box>

                  <Stack spacing={1.5}>
                    {selected.body.map((paragraph) => (
                      <Typography
                        key={paragraph}
                        variant="body2"
                        sx={{ color: "text.primary", lineHeight: 1.6 }}
                      >
                        {fillPreview(paragraph)}
                      </Typography>
                    ))}
                  </Stack>

                  {selected.cta && (
                    <Box
                      sx={{
                        mt: 3,
                        display: "inline-block",
                        px: 2,
                        py: 1,
                        borderRadius: 1.5,
                        backgroundColor: "#2563eb",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {selected.cta}
                    </Box>
                  )}

                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 3, color: "text.secondary" }}
                  >
                    {fillPreview(selected.previewText)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
